// Remote support on the web client — the HELPER's side (shared/REMOTE-SUPPORT.md §1, §6). The web never
// offers the request (it cannot inject input); it rings, accepts / declines, and views + controls.
//
// Fed by the worker's `remoteSupport` signal (plugin session + signal events, and "no live session" after a
// reconcile). Decisions:
//  - a `requested` session for which I am the helper rings (card + tone + background notification) until
//    accepted / declined here, answered elsewhere, or 60 s after it was created;
//  - accepting HERE opens the viewer (HelperSession + SupportViewer); a session that becomes live because
//    another of my devices accepted just stops ringing (and says so);
//  - a live session with no viewer here is "joinable": the DM row's «Unirse» opens the viewer and asks the
//    requester for a fresh offer (§3.1 `ready`);
//  - signals for a session whose viewer is not open yet are kept (the offer can beat the accept response)
//    and replayed in order when it opens;
//  - the end of the session (event, link loss, the requester's bye, «Terminar») closes the viewer and posts
//    `/end` once (idempotent on the server).
// No DOM here (the page wires tones/notifications through the backend), so it is unit-tested with a fake
// RTCPeerConnection.
import type { CallIceServer } from '../../../core/callIceServer';
import { isFinished, isLive, RING_MS, type SignalPayload, type SupportSession } from '../../../remotesupport/protocol';
import type { RtcEnvironment } from '../../../remotesupport/rtc';
import { HelperSession, type HelperViewState } from '../../../remotesupport/session';
import type { RemoteSupportSignal } from '../../../data/features/remoteSupport';

export type ActionResult = { ok: boolean; status?: number; session: SupportSession | null };

export interface SupportBackend {
  onSignal(listener: (signal: RemoteSupportSignal) => void): void;
  activeSession(): Promise<{ session: SupportSession | null; names: Record<string, string> }>;
  accept(id: string): Promise<ActionResult>;
  decline(id: string): Promise<ActionResult>;
  end(id: string, reason: string): Promise<ActionResult>;
  signal(id: string, payload: SignalPayload): Promise<void>;
  markActive(id: string): Promise<void>;
  iceServers(): Promise<CallIceServer[]>;
  /** The ring's sound + background notification (on) / their end (off). */
  ring(on: boolean, requesterName: string, requesterId: string): void;
  now(): number;
  later(fn: () => void, ms: number): () => void;
  log(line: string): void;
  rtc?: RtcEnvironment;
}

export type SupportNotice = 'taken' | 'acceptFailed' | 'ended' | 'endedLink' | 'endedByPeer' | null;

export interface SupportUiState {
  me: string;
  ring: { sessionId: string; requesterId: string; requesterName: string; channelId: string } | null;
  viewer: { sessionId: string; requesterId: string; requesterName: string } | null;
  /** Live sessions I help with and have no viewer for (the DM row's «Unirse»). */
  joinable: string[];
  notice: SupportNotice;
  /** The peer named in the notice. */
  noticeName: string;
}

const EMPTY: SupportUiState = { me: '', ring: null, viewer: null, joinable: [], notice: null, noticeName: '' };
const MAX_EARLY_SIGNALS = 200;

export class SupportManager {
  private state: SupportUiState = EMPTY;
  private readonly listeners = new Set<() => void>();
  private readonly viewListeners = new Set<(s: HelperViewState) => void>();
  private helper: HelperSession | null = null;
  private readonly sessions = new Map<string, SupportSession>();
  private readonly names: Record<string, string> = {};
  private readonly early = new Map<string, SignalPayload[]>();
  private readonly dismissed = new Set<string>();
  private acceptedHere: string | null = null;
  private cancelRingTimer: (() => void) | null = null;
  private wired = false;

  constructor(private readonly backend: SupportBackend) {}

  getState(): SupportUiState {
    return this.state;
  }

  /** The open viewer's session (the React viewer mounts SupportViewer on it). */
  get helperSession(): HelperSession | null {
    return this.helper;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  }

  subscribeView(fn: (s: HelperViewState) => void): () => void {
    this.viewListeners.add(fn);
    return () => void this.viewListeners.delete(fn);
  }

  /** Called with the signed-in user (and again on a user change). Asks for the live session once. */
  wire(me: string | null): void {
    if (!me || me === this.state.me) return;
    this.set({ ...EMPTY, me });
    if (!this.wired) {
      this.wired = true;
      this.backend.onSignal((s) => this.onSignal(s));
    }
    void this.backend.activeSession().then(({ session, names }) => {
      Object.assign(this.names, names);
      if (session) this.onSession(session);
    });
  }

  nameOf(id: string): string {
    return this.names[id] ?? '';
  }

  async accept(): Promise<void> {
    const ring = this.state.ring;
    if (!ring) return;
    this.acceptedHere = ring.sessionId;
    this.stopRing();
    this.backend.log(`[remote] accept ${ring.sessionId}`);
    const result = await this.backend.accept(ring.sessionId).catch(() => ({ ok: false, session: null }) as ActionResult);
    if (!result.ok) {
      this.acceptedHere = null;
      this.backend.log(`[remote] accept refused (${result.status ?? 'network'})`);
      this.set({ notice: 'acceptFailed', noticeName: ring.requesterName });
      if (result.session) this.onSession(result.session);
      return;
    }
    const session = result.session ?? this.sessions.get(ring.sessionId);
    if (session) this.onSession(session.state === 'requested' ? { ...session, state: 'connecting' } : session);
  }

  async decline(): Promise<void> {
    const ring = this.state.ring;
    if (!ring) return;
    this.dismissed.add(ring.sessionId);
    this.stopRing();
    this.backend.log(`[remote] decline ${ring.sessionId}`);
    const result = await this.backend.decline(ring.sessionId).catch(() => null);
    if (result?.session) this.onSession(result.session);
  }

  /** The DM row's «Unirse»: accept a pending request, or re-open the viewer of a live one. */
  join(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session || session.helperId !== this.state.me) return;
    if (session.state === 'requested') {
      this.ringFor(session);
      void this.accept();
    } else if (isLive(session.state) && this.state.viewer?.sessionId !== sessionId) {
      this.openViewer(session, true);
    }
  }

  /** «Terminar» in the viewer. */
  end(): void {
    const viewer = this.state.viewer;
    if (!viewer) return;
    this.closeViewer('helper');
    void this.backend.end(viewer.sessionId, 'helper');
  }

  consumeNotice(): void {
    if (this.state.notice) this.set({ notice: null, noticeName: '' });
  }

  // ───────────────────────── events ─────────────────────────

  private onSignal(s: RemoteSupportSignal): void {
    Object.assign(this.names, s.names);
    const event = s.event;
    if (event.kind === 'noActive') {
      for (const session of [...this.sessions.values()]) if (!isFinished(session.state)) this.onSession({ ...session, state: 'ended', endedReason: 'gone' });
      return;
    }
    if (event.kind === 'session') {
      this.onSession(event.session);
      return;
    }
    if (this.helper && this.helper.sessionId === event.sessionId) {
      this.helper.handleSignal(event.payload);
      return;
    }
    const queue = this.early.get(event.sessionId) ?? [];
    if (queue.length < MAX_EARLY_SIGNALS) queue.push(event.payload);
    this.early.set(event.sessionId, queue);
  }

  private onSession(session: SupportSession): void {
    const me = this.state.me;
    if (!me || (session.helperId !== me && session.requesterId !== me)) return;
    const previous = this.sessions.get(session.id);
    // An older record never overrides a newer one (a reconcile answer racing an event).
    if (previous && previous.updatedAt > session.updatedAt && session.updatedAt) return;
    this.sessions.set(session.id, session);
    // The web is never the requester: a session I asked for on another device is that device's business.
    if (session.helperId !== me) return;

    if (session.state === 'requested') {
      if (!this.dismissed.has(session.id) && this.acceptedHere !== session.id) this.ringFor(session);
    } else if (isLive(session.state)) {
      if (this.state.ring?.sessionId === session.id) {
        this.stopRing();
        if (this.acceptedHere !== session.id) this.set({ notice: 'taken', noticeName: this.nameOf(session.requesterId) });
      }
      if (this.acceptedHere === session.id && this.state.viewer?.sessionId !== session.id) this.openViewer(session, false);
    } else if (isFinished(session.state)) {
      if (this.state.ring?.sessionId === session.id) this.stopRing();
      if (this.state.viewer?.sessionId === session.id) {
        this.closeViewer(session.endedReason || 'ended');
        this.set({ notice: 'endedByPeer', noticeName: this.nameOf(session.requesterId) });
      }
      this.early.delete(session.id);
      if (this.acceptedHere === session.id) this.acceptedHere = null;
    }
    this.publishJoinable();
  }

  // ───────────────────────── ring ─────────────────────────

  private ringFor(session: SupportSession): void {
    const age = session.createdAt ? this.backend.now() - session.createdAt : 0;
    if (age >= RING_MS) return;
    if (this.state.ring?.sessionId === session.id) return;
    this.stopRing();
    const requesterName = this.nameOf(session.requesterId);
    this.set({ ring: { sessionId: session.id, requesterId: session.requesterId, requesterName, channelId: session.channelId } });
    this.backend.ring(true, requesterName, session.requesterId);
    this.backend.log(`[remote] ring ${session.id}`);
    this.cancelRingTimer = this.backend.later(() => this.stopRing(), Math.max(0, RING_MS - Math.max(0, age)));
  }

  private stopRing(): void {
    if (this.cancelRingTimer) this.cancelRingTimer();
    this.cancelRingTimer = null;
    if (!this.state.ring) return;
    this.backend.ring(false, '', '');
    this.set({ ring: null });
  }

  // ───────────────────────── viewer ─────────────────────────

  private openViewer(session: SupportSession, reopen: boolean): void {
    if (this.helper) this.closeViewer('replaced');
    const requesterName = this.nameOf(session.requesterId);
    this.backend.log(`[remote] viewer open ${session.id}${reopen ? ' (re-open)' : ''}`);
    const helper = new HelperSession(
      session.id,
      {
        postSignal: (id, payload) => this.backend.signal(id, payload),
        markActive: (id) => this.backend.markActive(id),
        iceServers: () => this.backend.iceServers(),
        log: (line) => this.backend.log(line),
      },
      {
        state: (s) => {
          for (const fn of this.viewListeners) fn(s);
        },
        ended: (reason) => {
          if (this.helper !== helper) return;
          this.helper = null;
          this.set({ viewer: null, notice: reason === 'link' || reason === 'failed' ? 'endedLink' : 'endedByPeer', noticeName: requesterName });
          void this.backend.end(session.id, reason.startsWith('bye') ? 'requester' : reason);
          this.publishJoinable();
        },
      },
      this.backend.rtc,
    );
    this.helper = helper;
    const queued = this.early.get(session.id) ?? [];
    this.early.delete(session.id);
    for (const p of queued) helper.handleSignal(p);
    this.set({ viewer: { sessionId: session.id, requesterId: session.requesterId, requesterName } });
    this.publishJoinable();
    void helper.start(reopen);
  }

  private closeViewer(reason: string): void {
    const helper = this.helper;
    this.helper = null;
    helper?.stop(reason);
    if (this.state.viewer) this.set({ viewer: null });
  }

  private publishJoinable(): void {
    const me = this.state.me;
    const joinable = [...this.sessions.values()]
      .filter((s) => s.helperId === me && (isLive(s.state) || s.state === 'requested') && this.state.viewer?.sessionId !== s.id)
      .map((s) => s.id)
      .sort();
    if (joinable.join() !== this.state.joinable.join()) this.set({ joinable });
  }

  private set(patch: Partial<SupportUiState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }
}
